import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, ChevronRight, Loader2, UserRound, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';

function accountOrganization(account) {
  return (
    account?.organization_name
    || account?.name
    || account?.workspace_name
    || account?.account_id
    || 'Workspace'
  );
}

function accountRoleLabel(account) {
  return (
    account?.role_label
    || (account?.effective_role || account?.role || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
    || 'Member'
  );
}

function accountPortalType(account) {
  return account?.portal_type || account?.portal || account?.type || account?.account_type || '';
}

function groupKeyForAccount(account) {
  const portal = String(accountPortalType(account) || '').toLowerCase();
  if (portal === 'platform') return 'platform';
  if (portal === 'self_repair' || portal === 'self_serve' || portal === 'self_serve_portal') {
    return 'personal';
  }
  if (portal === 'managed_client' || portal === 'client') return 'personal';
  return 'agency';
}

const GROUP_META = {
  personal: { title: 'Personal Portals', Icon: UserRound },
  agency: { title: 'Agency Workspaces', Icon: Building2 },
  platform: { title: 'Platform Access', Icon: Shield },
};

/**
 * Account chooser after single password authentication.
 * Labels come from API account contexts — never hardcode person/company names.
 */
export default function ChooseAccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectAccount,
    listAccounts,
    user,
    hasIdentity,
    awaitingAccountSelection,
    selectionChallenge,
    logout,
  } = useAuth();

  const bootAccounts = location.state?.accounts || selectionChallenge?.accounts;
  const bootToken =
    location.state?.selection_token
    || location.state?.challenge_id
    || selectionChallenge?.challenge_id;
  const bootUser = location.state?.user;

  const [accounts, setAccounts] = useState(() =>
    Array.isArray(bootAccounts) ? bootAccounts : []
  );
  const [selectionToken, setSelectionToken] = useState(bootToken || '');
  const [loading, setLoading] = useState(!Array.isArray(bootAccounts) || bootAccounts.length === 0);
  const [submittingId, setSubmittingId] = useState(null);
  const [error, setError] = useState('');

  const displayName = useMemo(() => {
    return (
      bootUser?.display_name
      || bootUser?.name
      || user?.display_name
      || user?.name
      || user?.email
      || ''
    );
  }, [bootUser, user]);

  const grouped = useMemo(() => {
    const buckets = { personal: [], agency: [], platform: [] };
    for (const account of accounts) {
      buckets[groupKeyForAccount(account)].push(account);
    }
    return ['personal', 'agency', 'platform']
      .map((key) => ({ key, ...GROUP_META[key], accounts: buckets[key] }))
      .filter((group) => group.accounts.length > 0);
  }, [accounts]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      // Challenge accounts are the source of truth when present — do not replace
      // them with a stale /auth/accounts snapshot.
      if (Array.isArray(bootAccounts) && bootAccounts.length > 0 && bootToken) {
        setAccounts(bootAccounts);
        setSelectionToken(bootToken);
        setLoading(false);
        return;
      }
      if (!hasIdentity && !awaitingAccountSelection) {
        navigate('/login', { replace: true });
        return;
      }
      try {
        setLoading(true);
        const data = await listAccounts();
        if (cancelled) return;
        setAccounts(Array.isArray(data.accounts) ? data.accounts : []);
        if (!bootToken) {
          setError('Account selection expired. Please sign in again.');
        }
      } catch (err) {
        if (!cancelled) {
          setError('Unable to load accounts. Please sign in again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [bootAccounts, bootToken, hasIdentity, awaitingAccountSelection, listAccounts, navigate]);

  const onSelect = useCallback(
    async (account) => {
      const accountId = account?.account_id || account?.account_context_id;
      if (!accountId || submittingId) return;
      if (!selectionToken) {
        setError('Account selection expired. Please sign in again.');
        navigate('/login', { replace: true });
        return;
      }
      setSubmittingId(accountId);
      setError('');
      try {
        const result = await selectAccount(accountId, selectionToken);
        const redirectTo = result?.redirect_to || result?.redirect_path || result?.session?.redirect_path;
        if (redirectTo && String(redirectTo).startsWith('/')) {
          navigate(redirectTo, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 401) {
          setError(typeof detail === 'string' ? detail : 'Account selection expired. Please sign in again.');
          toast.error('Account selection expired');
          try {
            await logout();
          } catch {
            /* ignore */
          }
          navigate('/login', { replace: true });
          return;
        }
        setError(
          typeof detail === 'string'
            ? detail
            : 'Unable to open that account. Please try again.'
        );
        toast.error('Account selection failed');
      } finally {
        setSubmittingId(null);
      }
    },
    [navigate, selectAccount, selectionToken, submittingId, logout]
  );

  const onKeyActivate = (event, account) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(account);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-card shadow-lg p-6 sm:p-8">
        <h1 className="text-2xl font-semibold tracking-tight">
          Choose how you want to continue
        </h1>
        {displayName ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Signed in as {displayName}. Pick the portal or workspace to open — no password needed again.
          </p>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">
            Select the portal or workspace you want to open.
          </p>
        )}

        <div className="mt-6 space-y-5" role="listbox" aria-label="Available accounts">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden />
              Loading accounts…
            </div>
          ) : accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6">
              This login is valid, but no active account is available. Contact your administrator.
            </p>
          ) : (
            grouped.map((group) => {
              const Icon = group.Icon;
              return (
                <div key={group.key} className="space-y-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1">
                    {group.title}
                  </h2>
                  {group.accounts.map((account) => {
                    const accountId = account.account_id || account.account_context_id;
                    const busy = submittingId === accountId;
                    const disabled = Boolean(submittingId);
                    return (
                      <button
                        key={accountId}
                        type="button"
                        role="option"
                        aria-selected={busy}
                        disabled={disabled}
                        onClick={() => onSelect(account)}
                        onKeyDown={(event) => onKeyActivate(event, account)}
                        className={cn(
                          'w-full text-left rounded-xl border border-border bg-background px-4 py-3',
                          'flex items-center gap-3 transition-colors',
                          'hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                          disabled && !busy ? 'opacity-60' : ''
                        )}
                      >
                        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-foreground">
                          <Icon className="h-5 w-5" aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">
                            {accountOrganization(account)}
                          </span>
                          <span className="block text-xs text-muted-foreground truncate">
                            {accountRoleLabel(account)}
                            {displayName ? ` · ${displayName}` : ''}
                          </span>
                        </span>
                        {busy ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-label="Opening account" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {error ? (
          <p className="mt-4 text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        <div className="mt-6 flex justify-between gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/login', { replace: true })}
          >
            Back to sign in
          </Button>
        </div>
      </div>
    </div>
  );
}
